/**
 * @fileoverview Step 2 - Grade and School selection
 * @description Allows user to select grade and school
 */

import { useState, useEffect, useCallback, forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from '@mantine/form'
import { Alert, Button, Space, Select, MultiSelect, Group, Text, Divider, useMantineTheme } from '@mantine/core'
import { useDebouncedValue } from '@mantine/hooks'
import { IconAlertCircle } from '@tabler/icons-react'
import { Heading } from 'components'
import { useNotification } from 'context'
import { useSignupContext } from '../context/SignupContext'
import { useGrades } from '../hooks/useGrades'
import { getExternalSchools, searchExternalSchools, completeOnboarding } from '../service'
import type { School } from '../service/types'
import { Form } from '../styles'
import { EmailVerificationModal } from './EmailVerificationModal'
import { GoogleSignupSuccessModal } from './GoogleSignupSuccessModal'

interface SchoolItemProps extends React.ComponentPropsWithoutRef<'div'> {
  label: string
  description?: string
}

/**
 * Custom Select item component
 * Shows school name with address in secondary line
 */
const SchoolSelectItem = forwardRef<HTMLDivElement, SchoolItemProps>(
  ({ label, description, ...others }, ref) => (
    <div ref={ref} {...others}>
      <Text size='sm' weight={500}>{label}</Text>
      {description && (
        <Text size='xs' color='dimmed' mt={2}>{description}</Text>
      )}
    </div>
  ),
)

SchoolSelectItem.displayName = 'SchoolSelectItem'

/**
 * Step 2 component - Grade and School selection
 */
export const SignupStep2 = () => {
  const { t } = useTranslation()
  const theme = useMantineTheme()
  const { handleNotification } = useNotification()
  const { data, setData, setStep, isGoogleAuth } = useSignupContext()
  const { gradeOptions, error: gradesError } = useGrades(t('teacher_signup.load_grades_error'))
  const [loading, setLoading] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [showEmailModal, setShowEmailModal] = useState(false)
  const [showGoogleModal, setShowGoogleModal] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [debouncedSearch] = useDebouncedValue(searchValue, 300)
  const [schools, setSchools] = useState<School[]>([])
  const [schoolsLoading, setSchoolsLoading] = useState(true)
  const [schoolsError, setSchoolsError] = useState<string | null>(null)

  const form = useForm({
    initialValues: {
      grades: data.grades ?? [],
      schoolId: data.schoolId ?? '',
    },
    validateInputOnBlur: true,
    validate: {
      grades: value => (!value || value.length === 0 ? t('teacher_signup.validation_grade_required') : null),
      schoolId: value => (!value ? t('teacher_signup.validation_school_required') : null),
    },
  })

  // Load schools: initial list when empty, search results when typing, reset to initial when cleared
  useEffect(() => {
    const controller = new AbortController()

    const loadSchools = async () => {
      setSchoolsLoading(true)
      if (!debouncedSearch) setSchoolsError(null)
      try {
        const result = debouncedSearch
          ? await searchExternalSchools(debouncedSearch)
          : await getExternalSchools()
        if (!controller.signal.aborted) setSchools(result)
      } catch (err) {
        if (controller.signal.aborted) return
        if (!debouncedSearch) {
          const errorMessage = err instanceof Error ? err.message : t('teacher_signup.load_schools_error')
          setSchoolsError(errorMessage)
          handleNotification(true, errorMessage, true, 'error')
        } else {
          console.error('Search failed:', err)
        }
      } finally {
        if (!controller.signal.aborted) setSchoolsLoading(false)
      }
    }

    loadSchools()
    return () => controller.abort()
  }, [debouncedSearch, t, handleNotification])

  const handleSearchChange = useCallback((value: string) => {
    setSearchValue(value)
  }, [])

  /**
   * Handle form submission - completes onboarding with school and grade
   */
  const handleSubmit = async (values: typeof form.values) => {
    setLoading(true)
    setSubmitError(null)

    try {
      await completeOnboarding(values.schoolId, values.grades)
      // Write to context only after success
      setData({ ...data, ...values })
      if (isGoogleAuth) {
        setShowGoogleModal(true)
      } else {
        setShowEmailModal(true)
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('teacher_signup.signup_error')
      setSubmitError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  /** Navigate to add new school step */
  const handleNewSchool = () => {
    setData({ grades: form.values.grades })
    setStep(3)
  }

  const schoolOptions = schools.map(school => {
    const parts: string[] = []
    if (school.street) parts.push(school.street)
    if (school.city) parts.push(school.city)
    const stateZip = [school.state, school.zipcode].filter(Boolean).join(' ')
    if (stateZip) parts.push(stateZip)

    return {
      value: school.id,
      label: school.name,
      description: parts.length > 0 ? parts.join(', ') : undefined,
    }
  })

  const selectedSchoolDescription = schoolOptions.find(s => s.value === form.values.schoolId)?.description

  const dropdownStyles = {
    dropdown: {
      borderRadius: theme.radius.md,
      border: `1px solid ${theme.colors.gray[3]}`,
      boxShadow: theme.shadows.sm,
      padding: theme.spacing.xs,
    },
    item: {
      padding: `${theme.spacing.xs}px ${theme.spacing.sm}px`,
      borderRadius: theme.radius.sm,
      '&[data-selected]': {
        backgroundColor: theme.colors.blue[6],
        '& *': { color: `${theme.white} !important` },
      },
      '&[data-hovered]': {
        backgroundColor: theme.colors.gray[1],
      },
    },
  }

  return (
    <>
      <EmailVerificationModal
        opened={showEmailModal}
        email={data.email}
        onClose={() => setShowEmailModal(false)}
      />
      <GoogleSignupSuccessModal
        opened={showGoogleModal}
        onClose={() => setShowGoogleModal(false)}
      />

      <div style={{ textAlign: 'center' }}>
        <Heading size='xl' text={t('teacher_signup.page_title')} />
      </div>
      <Text color='dimmed' align='center' mb='lg'>
        {t('teacher_signup.page_subtitle')}
      </Text>

      <Form onSubmit={form.onSubmit(handleSubmit)}>
        <MultiSelect
          size='md'
          label={t('teacher_signup.grade_label')}
          placeholder={t('teacher_signup.grade_placeholder')}
          data={gradeOptions}
          error={gradesError}
          styles={dropdownStyles}
          {...form.getInputProps('grades')}
        />

        <Space h='md' />

        <div>
          <Group position='apart' mb={4}>
            <Text size='sm' weight={500}>{t('teacher_signup.school_label')}</Text>
            <Button variant='subtle' compact onClick={handleNewSchool}>
              {t('teacher_signup.new_school_button')}
            </Button>
          </Group>
          <Select
            size='md'
            placeholder={t('teacher_signup.school_placeholder')}
            data={schoolOptions}
            searchable
            clearable
            searchValue={searchValue}
            onSearchChange={handleSearchChange}
            nothingFound={schoolsLoading ? t('common.loading') : t('teacher_signup.no_schools_found')}
            filter={() => true}
            description={selectedSchoolDescription}
            inputWrapperOrder={['label', 'input', 'description', 'error']}
            error={schoolsError}
            itemComponent={SchoolSelectItem}
            maxDropdownHeight={320}
            styles={{
              ...dropdownStyles,
              input: {
                fontWeight: form.values.schoolId ? 500 : undefined,
              },
            }}
            {...form.getInputProps('schoolId')}
          />
        </div>

        {submitError && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title={t('teacher_signup.signup_error')}
            color='red'
            mt='lg'
            withCloseButton
            onClose={() => setSubmitError(null)}
          >
            {submitError}
          </Alert>
        )}

        <Divider my='lg' />

        <Button
          fullWidth
          size='lg'
          type='submit'
          loading={loading}
          radius='xl'
        >
          {t('teacher_signup.sign_up_button')}
        </Button>
      </Form>
    </>
  )
}
